import { pageCreditLabelKey } from '@/components/contributor/credit-label';
import ResourcesCarousel from '@/components/resources-carousel';
import { useHumanReadable } from '@/hooks/use-human-readable';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { averagePackGuideMinutes } from '@/utils/guides';
import { MediaGuideMetadata } from '@/utils/media/types';
import { ContributorDialog } from '@rhg/components/contributor/dialog';
import { ContributorListItem } from '@rhg/components/contributor/list-item';
import {
    ContributorSelection,
    ContributorType,
} from '@rhg/components/contributor/types';
import { resolveCreditGroups } from '@rhg/components/media/audio-player/credits';
import HasResourcesConfigured from '@rhg/components/resources/hasResourcesConfigured/index';
import { MaxColumnsContainer } from '@rhg/components/ui/layout';
import { useProgramActions } from '@rhg/hooks/use-program-actions';
import { useProvidedProgramContextWithWorkflow } from '@rhg/hooks/use-provided-program-context/with-workflow';
import commonMessages from '@rhg/lang/messages/common';
import programMessages from '@rhg/lang/messages/program';
import {
    SxProps,
    Box,
    Divider,
    Icon,
    Paper,
    Theme,
    Typography,
    Grid,
} from '@rhg/libs/material';
import { TimeOutline, TotalSessionsOutline } from '@rhg/libs/material/icons';
import NextImage from '@rhg/libs/next/image';
import { useAudioControls } from '@rhg/providers/guide-player/active-player/use-audio-controls';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ProgramNextButton } from '../next-button';

const styleDivider = {
    width: '100%',
    my: (theme: Theme) => theme.ds.spacingUnits.CONTENT,
};
const styleNextButton = { width: '100%' };
const styleSummaryContainer = {
    mt: 4,
    mb: 14,
};
const styleWhatYoullGet: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    width: '100%',
    p: (theme: Theme) => theme.ds.spacingUnits.CONTENT,
    mb: (theme: Theme) => theme.ds.spacingUnits.CONTENT,
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? theme.palette.background.paper
            : theme.palette.mask.black['20p'],
};
const styleProgramStats = {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
};
const styleProgramStat = {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    flexGrow: 1,
};
const styleProgramStatIcon = { color: 'text.sub' };

export function ProgramOverview() {
    const isMobile = useIsMobile();
    const { roundedDuration } = useHumanReadable();
    const { program, programType, numSessions, workflow } =
        useProvidedProgramContextWithWorkflow();
    const { handleNextButton } = useProgramActions();
    const [selectedContributor, setSelectedContributor] =
        useState<ContributorSelection>();

    const { formatMessage } = useIntl();

    const firstGuide = useMemo(
        () =>
            program?.guides?.find((g) => !g.isTrailer) ??
            program?.guides?.[0] ??
            ({} as MediaGuideMetadata),
        [program?.guides]
    );

    const controls = useAudioControls({ program, playingGuide: firstGuide });

    const creditGroups = useMemo(
        () => resolveCreditGroups(controls, program?.narrator, program?.author),
        [controls, program?.narrator, program?.author]
    );

    const categoryIcon = useMemo(() => {
        return programType === 'clinical_program' ? (
            <NextImage
                src="/_n/images/ch-grad.png"
                alt=""
                width={64}
                height={64}
            />
        ) : undefined;
    }, [programType]);

    const { max, min } = useMemo(() => {
        const { max, min } = averagePackGuideMinutes(program?.guides ?? []);
        return {
            max: roundedDuration(max),
            min: roundedDuration(min),
        };
    }, [program?.guides, roundedDuration]);

    const commonTitleKey =
        programType === 'clinical_program'
            ? 'common.program_type.clinical_program.title'
            : 'common.program_type.undefined.title';
    const commonDescriptionKey =
        programType === 'clinical_program'
            ? 'common.program_type.clinical_program.description'
            : 'common.program_type.undefined.description';

    return (
        <Grid container justifyContent="center">
            <Grid item xs={12}>
                {creditGroups.map((group) => {
                    const contributor = group.members[0];
                    if (!contributor) return null;
                    const contributorType =
                        group.role === 'author'
                            ? ContributorType.Author
                            : ContributorType.Narrator;
                    return (
                        <ContributorListItem
                            key={group.labelKey}
                            clickable
                            trackButton={
                                group.role === 'author'
                                    ? 'ViewProgramAuthor'
                                    : 'ViewProgramNarrator'
                            }
                            contributor={contributor}
                            contributorType={contributorType}
                            roleLabel={formatMessage(
                                commonMessages[
                                    pageCreditLabelKey(group.labelKey)
                                ]
                            )}
                            onClick={() =>
                                setSelectedContributor({
                                    contributor,
                                    contributorType,
                                })
                            }
                        />
                    );
                })}
            </Grid>
            {isMobile && (
                <>
                    <Divider
                        aria-hidden
                        orientation="horizontal"
                        sx={styleDivider}
                    />
                    <MaxColumnsContainer columns={6}>
                        <ProgramNextButton
                            trackButton="ProgramOverviewNext"
                            sx={styleNextButton}
                            onClick={handleNextButton()}
                        />
                    </MaxColumnsContainer>
                </>
            )}
            <Divider aria-hidden orientation="horizontal" sx={styleDivider} />
            <Grid item xs={12} sx={styleSummaryContainer}>
                <Typography
                    variant="h5"
                    component="h2"
                    color="text.body"
                    mb={1}
                >
                    {formatMessage(commonMessages['common.summary'])}
                </Typography>
                <Typography variant="subtitle1" color="text.sub" component="p">
                    {program?.description}
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography
                    textAlign="left"
                    variant="h5"
                    component="h2"
                    color="text.body"
                    mb={8}
                >
                    {formatMessage(programMessages['program.what_youll_get'])}
                </Typography>
            </Grid>
            <Grid item xs={12} mb={4}>
                <Paper sx={styleWhatYoullGet}>
                    <Box mb={2}>{categoryIcon}</Box>
                    <Typography
                        color="primary"
                        variant="body1Demi"
                        component="h3"
                        mb={1}
                    >
                        {formatMessage(commonMessages[commonTitleKey])}
                    </Typography>
                    <Typography
                        textAlign="center"
                        color="text.sub"
                        variant="subtitle2"
                        component="p"
                    >
                        {formatMessage(commonMessages[commonDescriptionKey])}
                    </Typography>
                    <Divider
                        aria-hidden
                        orientation="horizontal"
                        sx={styleDivider}
                    />
                    <MaxColumnsContainer columns={4}>
                        <Box sx={styleProgramStats}>
                            <Box sx={styleProgramStat}>
                                <Icon sx={styleProgramStatIcon}>
                                    <TotalSessionsOutline />
                                </Icon>
                                <Typography
                                    mb={2}
                                    mt={1}
                                    variant="caption"
                                    color="text.body"
                                >
                                    {formatMessage(
                                        commonMessages['common.total_sessions']
                                    )}
                                </Typography>
                                <Typography variant="caption" color="text.body">
                                    {numSessions}
                                </Typography>
                            </Box>
                            <Box sx={styleProgramStat}>
                                <Icon sx={styleProgramStatIcon}>
                                    <TimeOutline />
                                </Icon>
                                <Typography
                                    mb={2}
                                    mt={1}
                                    variant="caption"
                                    color="text.body"
                                >
                                    {formatMessage(
                                        commonMessages['common.session_minutes']
                                    )}
                                </Typography>
                                <Typography variant="caption" color="text.body">
                                    {formatMessage(
                                        commonMessages[
                                            'common.session_minutes_description'
                                        ],
                                        {
                                            min,
                                            max,
                                        }
                                    )}
                                </Typography>
                            </Box>
                        </Box>
                    </MaxColumnsContainer>
                </Paper>
            </Grid>
            <HasResourcesConfigured withThemes={true} themes={workflow?.themes}>
                <ResourcesCarousel
                    variant="smart"
                    themes={workflow?.themes}
                    source="ProgramOverview"
                />
            </HasResourcesConfigured>
            <Typography color="text.sub" variant="subtitle2" component="p">
                {formatMessage(commonMessages['common.clinical_disclaimer'])}
            </Typography>
            <ContributorDialog
                isOpen={!!selectedContributor}
                contributor={selectedContributor?.contributor}
                contributorType={selectedContributor?.contributorType}
                onClose={() => setSelectedContributor(undefined)}
            />
        </Grid>
    );
}
